<template>
  <div class="be-map__frame">
    <div class="be-map__viewport">
      <div
        class="be-map__ratio"
        :class="{ 'be-map__ratio--pannable': zoom > MIN_ZOOM, 'be-map__ratio--dragging': is_dragging }"
        :style="ratioStyle"
        ref="viewport"
        @mousedown="onPointerDown"
        @touchstart="onTouchStart"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd"
        @touchcancel="onTouchEnd"
        @wheel="onWheel">
        <div class="be-map__transform" :class="transformClass" :style="transformStyle">
          <img :src="image.image_url" class="be-map__img" alt="" />

          <svg class="be-map__svg" viewBox="0 0 100 100" preserveAspectRatio="none">
            <path
              v-for="shape in renderAreas"
              :key="shape.key"
              :d="shape.path"
              vector-effect="non-scaling-stroke"
              class="be-map__area"
              :class="shape.areaClass"
              @click.stop="onAreaClick(shape.item)"></path>
          </svg>

          <div
            v-for="shape in renderAreas"
            :key="'marker-' + shape.key"
            class="be-map__marker"
            :class="shape.markerClass"
            :style="shape.markerStyle"
            @mouseenter="onMarkerEnter(shape)"
            @mouseleave="onMarkerLeave(shape)"
            @click.stop="onMarkerClick(shape.item)">
            <!-- Name only: the pin has to stay small enough that a dense map is
                 still readable, so everything else lives in the hover card. -->
            <div class="be-map__pin">
              <span class="be-map__pin-icon">
                <b-spinner v-if="shape.item.isPending" small></b-spinner>
                <i v-else :class="shape.iconClass"></i>
              </span>
              <span class="be-map__pin-text">
                <span class="be-map__pin-title text-truncate">{{ shape.pinTitle }}</span>
              </span>
              <span v-if="shape.item.clickable" class="be-map__pin-chevron">
                <i class="simple-icon-arrow-right"></i>
              </span>
            </div>

            <transition name="be-map-preview">
              <div
                class="be-map__preview"
                :class="shape.previewClass"
                v-if="shape.canPreview && hovered_area_id === shape.key">
                <div class="be-map__preview-media" v-if="shape.item.preview.image">
                  <img :src="shape.item.preview.image" class="be-map__preview-img" alt="" />
                </div>
                <div class="be-map__preview-body">
                  <p class="be-map__preview-title text-truncate">{{ shape.item.labelTitle }}</p>
                  <p class="be-map__preview-price" v-if="shape.item.preview.priceFrom">
                    <span class="be-map__preview-from">{{ $t('hotel.map-preview-from') }}</span>
                    {{ shape.item.preview.priceFrom }}
                  </p>
                  <p class="be-map__preview-meta" v-if="shape.item.preview.meta">
                    {{ shape.item.preview.meta }}
                  </p>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>

    <!-- Deliberately a sibling of ".be-map__viewport", not nested inside it:
         that box scrolls internally for a tall image, so controls placed inside
         would drift away with the scroll position. Anchoring them to this outer
         position:relative frame keeps them pinned to the visible corner. -->
    <div class="be-map__controls" @mousedown.stop @touchstart.stop>
      <!-- Reset goes first on purpose: .be-map__controls is bottom-anchored, so
           its box grows upward as buttons are added/removed. Inserting reset
           above zoom-in/zoom-out keeps their own screen position fixed whether
           reset is shown or not -- appending it last (below zoom-out) would
           shift both of them up the moment it appears, landing a fast
           second zoom-in click on zoom-out instead. -->
      <button
        type="button"
        class="be-map__control be-press"
        v-if="zoom > MIN_ZOOM"
        @click.stop="resetZoom">
        <i class="simple-icon-refresh"></i>
      </button>
      <!-- Step buttons are the only way to zoom with a mouse, so desktop keeps them.
           On a touchscreen pinch already does the job, and the buttons were just
           eating into the map's already-tight mobile real estate. -->
      <button
        type="button"
        class="be-map__control be-map__control--step be-press"
        :disabled="zoom >= MAX_ZOOM"
        @click.stop="zoomIn">
        <i class="simple-icon-magnifier-add"></i>
      </button>
      <button
        type="button"
        class="be-map__control be-map__control--step be-press"
        :disabled="zoom <= MIN_ZOOM"
        @click.stop="zoomOut">
        <i class="simple-icon-magnifier-remove"></i>
      </button>
    </div>
  </div>
</template>

<script>
  import { getPolygonLabelPoint, toRoundedSvgPath } from './polygonLabel.js';

  const MIN_ZOOM = 1;
  const MAX_ZOOM = 3;
  const ZOOM_STEP = 0.5;
  /* Pointer travel (px) that turns a press into a drag, and the larger travel
     past which the trailing click is swallowed instead of read as a tap. */
  const DRAG_THRESHOLD = 3;
  const CLICK_CANCEL_THRESHOLD = 6;
  // normalised y above which a hover card must open downward instead of upward
  const PREVIEW_FLIP_THRESHOLD = 0.32;

  export default {
    name: 'MapViewport',
    props: {
      image: {
        type: Object,
        default: () => ({})
      },
      areas: {
        type: Array,
        default: () => []
      },
      // changes whenever a different map is shown, so the viewport resets
      resetKey: {
        type: [String, Number],
        default: null
      }
    },
    data(){
      return {
        MIN_ZOOM: MIN_ZOOM,
        MAX_ZOOM: MAX_ZOOM,
        zoom: MIN_ZOOM,
        pan: { x: 0, y: 0 },
        is_dragging: false,
        pointer_start: null,
        pinch_start_distance: null,
        pinch_start_zoom: null,
        pinch_rect: null,
        /* Set when a drag/pinch moved past the click threshold, so the click that
           still fires on release in some browsers gets swallowed instead of being
           mistaken for a tap on an area, marker, or the background. */
        suppress_click: false,
        // desktop-only: on touch there is no hover, a tap opens the popup instead
        hovered_area_id: null
      }
    },
    computed: {
      ratioStyle(){
        /* Full width at the image's own aspect ratio -- never squeezed. The outer
           ".be-map__viewport" caps how much vertical space a tall map takes by
           scrolling internally instead. */
        const width = this.image && this.image.image_width ? this.image.image_width : 1;
        const height = this.image && this.image.image_height ? this.image.image_height : 1;
        return { aspectRatio: width + ' / ' + height };
      },
      transformStyle(){
        return {
          transform: 'scale(' + this.zoom + ') translate(' + this.pan.x + 'px, ' + this.pan.y + 'px)'
        };
      },
      renderAreas(){
        // Markers live inside the scaled/panned .be-map__transform box (so their
        // left/top % stays pinned to the right spot on the image), but they must
        // read the same size at any zoom level -- the inverse scale here cancels
        // the ancestor's scale(zoom) back out for the marker itself.
        const counterScale = 1 / this.zoom;
        return this.areas
          .filter((item) => item.area && Array.isArray(item.area.coordinates) && item.area.coordinates.length > 0)
          .map((item) => {
            const anchor = this.labelPointFor(item.area);
            return {
              key: item.area.id,
              item: item,
              canPreview: !!item.preview,
              /* .be-map__ratio clips its overflow, so a card opening upward from
                 a pin near the top edge would be cut in half -- flip it below. */
              previewClass: anchor.y < PREVIEW_FLIP_THRESHOLD ? 'be-map__preview--below' : '',
              path: toRoundedSvgPath(item.area.coordinates),
              areaClass: this.buildAreaClass(item),
              pinTitle: item.isPending ? this.$t('hotel.map-pin-loading') : item.labelTitle,
              markerClass: {
                'be-map__marker--link': item.kind == 'map_link',
                'be-map__marker--pending': item.isPending,
                'be-map__marker--muted': !item.clickable && !item.isPending
              },
              markerStyle: {
                left: anchor.x * 100 + '%',
                top: anchor.y * 100 + '%',
                transform: 'translate(-50%, -50%) scale(' + counterScale + ')'
              },
              iconClass: this.buildIconClass(item)
            };
          });
      },
      // True while a raw pointer gesture (single-finger drag or two-finger
      // pinch) tracks input in real time -- easing the transform during
      // either would make the map visibly lag behind the finger/cursor.
      isGestureTracking(){
        return !!this.pointer_start || !!this.pinch_start_distance;
      },
      transformClass(){
        return { 'be-map__transform--instant': this.isGestureTracking };
      }
    },
    watch: {
      resetKey(){
        this.resetViewport();
      }
    },
    created(){
      /* Plain object, not in data() -- a memo cache for a grid search, not view
         state, so it does not need Vue's reactivity. Area coordinates never
         change at runtime, but resolvedAreas recomputes on every cart change. */
      this.labelCache = {};
    },
    beforeDestroy(){
      this.detachPointerListeners();
    },
    methods: {
      labelPointFor(area){
        if(this.labelCache[area.id]) return this.labelCache[area.id];
        const point = getPolygonLabelPoint(area.coordinates) || { x: 0.5, y: 0.5 };
        this.labelCache[area.id] = point;
        return point;
      },
      buildAreaClass(item){
        if(item.kind == 'map_link') return 'be-map__area--link';
        if(item.isPending) return 'be-map__area--pending';
        if(!item.clickable) return 'be-map__area--muted';
        return 'be-map__area--available';
      },
      buildIconClass(item){
        if(item.kind == 'map_link') return 'simple-icon-arrow-right';
        return 'simple-icon-location-pin';
      },
      onMarkerEnter(shape){
        if(this.$is_mobile_view || !shape.canPreview) return;
        this.hovered_area_id = shape.key;
      },
      onMarkerLeave(shape){
        if(this.hovered_area_id === shape.key) this.hovered_area_id = null;
      },
      onAreaClick(item){
        if(this.consumeSuppressedClick()) return;
        if(!item.clickable) return;
        this.$emit('select-area', item);
      },
      onMarkerClick(item){
        // The pin always shows its label, so a single tap/click can act right
        // away -- no reveal-then-act step needed on mobile.
        if(this.consumeSuppressedClick()) return;
        if(item.clickable) this.$emit('select-area', item);
      },
      consumeSuppressedClick(){
        if(!this.suppress_click) return false;
        this.suppress_click = false;
        return true;
      },
      resetViewport(){
        this.zoom = MIN_ZOOM;
        this.pan = { x: 0, y: 0 };
        this.hovered_area_id = null;
        this.pointer_start = null;
        this.pinch_start_distance = null;
        this.pinch_start_zoom = null;
        this.is_dragging = false;
      },
      setZoom(next, width, height){
        this.zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
        this.clampPan(width, height);
      },
      zoomIn(){
        this.setZoom(this.zoom + ZOOM_STEP);
      },
      zoomOut(){
        this.setZoom(this.zoom - ZOOM_STEP);
      },
      resetZoom(){
        this.zoom = MIN_ZOOM;
        this.pan = { x: 0, y: 0 };
      },
      clampPan(width, height){
        let boxWidth = width;
        let boxHeight = height;
        if(!boxWidth || !boxHeight){
          if(!this.$refs.viewport) return;
          boxWidth = this.$refs.viewport.offsetWidth;
          boxHeight = this.$refs.viewport.offsetHeight;
        }
        if(!boxWidth || !boxHeight || this.zoom <= MIN_ZOOM){
          this.pan = { x: 0, y: 0 };
          return;
        }
        // Keeps the scaled content covering the viewport at all times.
        const maxPanX = (boxWidth * (this.zoom - 1)) / 2 / this.zoom;
        const maxPanY = (boxHeight * (this.zoom - 1)) / 2 / this.zoom;
        this.pan.x = Math.min(maxPanX, Math.max(-maxPanX, this.pan.x));
        this.pan.y = Math.min(maxPanY, Math.max(-maxPanY, this.pan.y));
      },
      startDrag(x, y){
        /* Clear a leftover suppress flag from a gesture whose click never came
           (mouse released outside the window) -- it would swallow the next tap. */
        this.suppress_click = false;
        let width = 0;
        let height = 0;
        if(this.$refs.viewport){
          width = this.$refs.viewport.offsetWidth;
          height = this.$refs.viewport.offsetHeight;
        }
        this.pointer_start = {
          x: x,
          y: y,
          panX: this.pan.x,
          panY: this.pan.y,
          moved: 0,
          width: width,
          height: height
        };
      },
      dragTo(x, y){
        if(!this.pointer_start) return;
        const deltaX = x - this.pointer_start.x;
        const deltaY = y - this.pointer_start.y;
        this.pointer_start.moved = Math.max(
          this.pointer_start.moved,
          Math.abs(deltaX),
          Math.abs(deltaY)
        );
        if(this.pointer_start.moved > DRAG_THRESHOLD){
          this.is_dragging = true;
          // the card would otherwise trail the pointer across the map mid-drag
          this.hovered_area_id = null;
        }
        this.pan.x = this.pointer_start.panX + deltaX / this.zoom;
        this.pan.y = this.pointer_start.panY + deltaY / this.zoom;
        this.clampPan(this.pointer_start.width, this.pointer_start.height);
      },
      endDrag(){
        if(this.pointer_start && this.pointer_start.moved > CLICK_CANCEL_THRESHOLD){
          this.suppress_click = true;
        }
        this.pointer_start = null;
        this.is_dragging = false;
      },
      detachPointerListeners(){
        window.removeEventListener('mousemove', this.onPointerMove);
        window.removeEventListener('mouseup', this.onPointerUp);
      },
      onPointerDown(e){
        if(e.button !== undefined && e.button !== 0) return;
        if(this.zoom <= MIN_ZOOM) return;
        this.startDrag(e.clientX, e.clientY);
        window.addEventListener('mousemove', this.onPointerMove);
        window.addEventListener('mouseup', this.onPointerUp);
      },
      onPointerMove(e){
        this.dragTo(e.clientX, e.clientY);
      },
      onPointerUp(){
        this.detachPointerListeners();
        this.endDrag();
      },
      touchDistance(touches){
        const deltaX = touches[0].clientX - touches[1].clientX;
        const deltaY = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      },
      onTouchStart(e){
        if(e.touches.length == 2){
          // Two-finger pinch takes over -- cancel any single-finger pan.
          this.pointer_start = null;
          this.suppress_click = false;
          this.pinch_start_distance = this.touchDistance(e.touches);
          this.pinch_start_zoom = this.zoom;
          if(this.$refs.viewport){
            this.pinch_rect = {
              width: this.$refs.viewport.offsetWidth,
              height: this.$refs.viewport.offsetHeight
            };
          }
          return;
        }
        if(e.touches.length == 1 && this.zoom > MIN_ZOOM){
          this.startDrag(e.touches[0].clientX, e.touches[0].clientY);
        }
      },
      onTouchMove(e){
        if(this.pinch_start_distance && e.touches.length == 2){
          const ratio = this.touchDistance(e.touches) / this.pinch_start_distance;
          const width = this.pinch_rect ? this.pinch_rect.width : 0;
          const height = this.pinch_rect ? this.pinch_rect.height : 0;
          this.setZoom(this.pinch_start_zoom * ratio, width, height);
          this.suppress_click = true;
          e.preventDefault();
          return;
        }
        if(this.pointer_start && e.touches.length == 1){
          this.dragTo(e.touches[0].clientX, e.touches[0].clientY);
          e.preventDefault();
        }
      },
      onTouchEnd(e){
        if(e.touches.length == 0){
          this.pinch_start_distance = null;
          this.pinch_start_zoom = null;
          this.endDrag();
          return;
        }
        // Two fingers down to one: end the pinch without starting a fresh drag
        // from the remaining finger, which would jump the map.
        this.pinch_start_distance = null;
        this.pinch_start_zoom = null;
        this.pointer_start = null;
      },
      onWheel(e){
        if(!e.shiftKey){
          // Plain scroll over the map must never hijack the page scroll.
          return;
        }
        /* Most browsers remap Shift+wheel into a horizontal scroll before
           dispatching: deltaY arrives as 0 and the movement lands in deltaX. */
        const rawDelta = e.deltaY !== 0 ? e.deltaY : e.deltaX;
        const isZoomingIn = rawDelta < 0;
        if(!isZoomingIn && this.zoom <= MIN_ZOOM){
          // Already at rest and scrolling further out -- let the page have it.
          return;
        }
        e.preventDefault();
        this.setZoom(this.zoom + (isZoomingIn ? ZOOM_STEP : -ZOOM_STEP));
      }
    }
  }
</script>

<style scoped>
.be-map__frame {
  position: relative;
  width: 100%;
}
.be-map__viewport {
  width: 100%;
  overflow: hidden;
  border-radius: var(--be-v3-radius, 12px);
}
/* Always fills the column's full width -- height follows from the image's own
   aspect-ratio (set inline via ratioStyle), so a wide map never leaves empty
   space beside it. No height cap: the trade-off is a taller box on narrow
   viewports for a very tall (portrait) map, which the guest can still pan. */
.be-map__ratio {
  position: relative;
  width: 100%;
  height: auto;
  overflow: hidden;
}
.be-map__ratio--pannable {
  cursor: grab;
  touch-action: none;
}
.be-map__ratio--dragging {
  cursor: grabbing;
}
.be-map__transform {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transform-origin: center center;
  /* Must match .be-map__marker's transition exactly (var + duration + easing)
     -- markers counter-scale against this element's zoom inline, a mismatched
     curve here makes pins visibly drift off their anchors mid-zoom. */
  transition: transform var(--be-v3-dur-fast, 150ms) var(--be-v3-ease, ease-out);
  will-change: transform;
}
.be-map__transform--instant {
  transition: none;
}
.be-map__img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: fill;
  user-select: none;
  -webkit-user-drag: none;
}
.be-map__svg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.be-map__area {
  stroke-width: 2;
  /* Rounds the polygon's own vertices -- an SVG polygon has no border-radius,
     this is the equivalent trick for softening its corners. */
  stroke-linejoin: round;
  stroke-linecap: round;
  transition: fill-opacity var(--be-v3-dur-fast, 150ms) var(--be-v3-ease, ease-out);
}
.be-map__area--available {
  fill: var(--merchant-primary-color, #79a33a);
  fill-opacity: 0.16;
  stroke: var(--merchant-primary-color, #79a33a);
  stroke-opacity: 0.65;
  cursor: pointer;
}
.be-map__area--available:hover {
  fill-opacity: 0.38;
}
/* Still waiting on its room-type detail: shown in the available colour but
   dimmed, so a map does not look half-broken while the pins fill in. */
.be-map__area--pending {
  fill: var(--merchant-primary-color, #79a33a);
  fill-opacity: 0.08;
  stroke: var(--merchant-primary-color, #79a33a);
  stroke-opacity: 0.3;
  cursor: progress;
}
.be-map__area--link {
  fill: #1a1a2e;
  fill-opacity: 0.12;
  stroke: #1a1a2e;
  stroke-opacity: 0.55;
  cursor: pointer;
}
.be-map__area--link:hover {
  fill-opacity: 0.3;
}
.be-map__area--muted {
  fill: #adb5bd;
  fill-opacity: 0.16;
  stroke: #adb5bd;
  stroke-opacity: 0.45;
  cursor: default;
}

/* The label is always visible now -- a small centred dot was ambiguous on its
   own, so the pin IS the label: icon + room name, readable without hovering or
   tapping first. Price and specs are reserved for the hover card. */
.be-map__marker {
  position: absolute;
  /* base translate/scale come from the inline style (markerStyle) so the
     counter-scale can track zoom -- this transition just smooths it out. */
  transform-origin: center center;
  transition: transform var(--be-v3-dur-fast, 150ms) var(--be-v3-ease, ease-out);
  z-index: 5;
  cursor: pointer;
}
.be-map__marker--muted {
  cursor: default;
}
.be-map__marker--pending {
  cursor: progress;
}
.be-map__marker:hover {
  z-index: 20;
}
.be-map__pin {
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 168px;
  padding: 6px 10px;
  border-radius: var(--be-v3-button-radius, 999px);
  background: var(--merchant-primary-color, #79a33a);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.22);
  color: #fff;
  transition: transform var(--be-v3-dur-fast, 150ms) var(--be-v3-ease, ease-out);
}
.be-map__marker:hover .be-map__pin {
  transform: translateY(-2px);
}
.be-map__pin-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.24);
}
.be-map__pin-icon i {
  font-size: 10px;
}
.be-map__pin-icon .spinner-border {
  width: 10px;
  height: 10px;
  border-width: 2px;
}
.be-map__pin-text {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  flex-direction: column;
  line-height: 1.25;
}
.be-map__pin-title {
  font-size: 0.75rem;
  font-weight: 700;
}
.be-map__pin-chevron {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  font-size: 9px;
  opacity: 0.85;
}
.be-map__marker--link .be-map__pin {
  background: #1a1a2e;
}
.be-map__marker--muted .be-map__pin {
  background: #adb5bd;
  box-shadow: none;
}
/* A pin with no room type yet reads as a placeholder rather than a broken
   area -- spinner instead of the location icon, and a "Loading..." label
   instead of a blank name, so it's clearly still fetching rather than stuck. */
.be-map__marker--pending .be-map__pin {
  background: var(--merchant-primary-color, #79a33a);
  opacity: 0.7;
  animation: be-map-pin-pulse 1.2s ease-in-out infinite;
}
.be-map__marker--pending .be-map__pin-title {
  font-style: italic;
  opacity: 0.85;
}
@keyframes be-map-pin-pulse {
  0%,
  100% {
    opacity: 0.55;
  }
  50% {
    opacity: 0.85;
  }
}

/* HOVER PREVIEW ----------------------------------------------------------
   Sits inside the marker so it inherits the inline counter-scale and stays a
   constant size at any zoom. pointer-events:none is what keeps it from eating
   the drag/pinch gestures or flickering when the cursor crosses onto it. */
.be-map__preview {
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  z-index: 40;
  width: 208px;
  transform: translateX(-50%);
  border-radius: var(--be-v3-radius, 12px);
  background: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.22);
  overflow: hidden;
  pointer-events: none;
}
.be-map__preview--below {
  bottom: auto;
  top: calc(100% + 10px);
}
.be-map__preview-media {
  width: 100%;
  height: 104px;
  background: #f4f5f7;
}
.be-map__preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.be-map__preview-body {
  padding: 8px 10px 10px;
}
.be-map__preview-title {
  margin-bottom: 2px;
  font-family: var(--be-v3-title-font, BeV3Sans), sans-serif;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #1a1a2e;
}
/* "From" price, not the searched rate: room-type takes no stay dates, so the
   accurate figure only appears once the popup runs a filtered search. */
.be-map__preview-price {
  margin-bottom: 2px;
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--merchant-primary-color, #79a33a);
}
.be-map__preview-from {
  margin-right: 3px;
  font-size: 0.6875rem;
  font-weight: 500;
  color: #6d6d6d;
}
.be-map__preview-meta {
  margin-bottom: 0;
  font-size: 0.6875rem;
  color: #6d6d6d;
}
.be-map-preview-enter-active,
.be-map-preview-leave-active {
  transition: opacity var(--be-v3-dur-fast, 150ms) var(--be-v3-ease, ease-out),
    transform var(--be-v3-dur-fast, 150ms) var(--be-v3-ease, ease-out);
}
.be-map-preview-enter,
.be-map-preview-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(4px);
}

/* Outside the transformed wrapper (so they never scale with the map) and
   outside the scrolling viewport (so they stay pinned to the visible corner). */
.be-map__controls {
  position: absolute;
  right: 10px;
  bottom: 10px;
  z-index: 30;
}
.be-map__control {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  margin-bottom: 6px;
  padding: 0;
  border: 1px solid var(--be-v3-border, #d5d9e0);
  border-radius: 50%;
  background: #fff;
  color: #1a1a2e;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.16);
  cursor: pointer;
}
.be-map__control:last-child {
  margin-bottom: 0;
}
.be-map__control i {
  font-size: 12px;
}
.be-map__control:disabled {
  opacity: 0.4;
  cursor: default;
}
.be-map__control:disabled.be-press:hover {
  transform: none;
}

/* A full-size pin label was designed for a wide desktop map -- on a phone
   viewport the same pin covers a much bigger share of the image, crowding out
   neighbouring rooms. Shrink the chrome rather than hide the label. */
@media screen and (max-width: 767.98px) {
  .be-map__pin {
    max-width: 120px;
    padding: 4px 8px;
    gap: 4px;
  }
  .be-map__pin-icon {
    width: 16px;
    height: 16px;
  }
  .be-map__pin-icon i {
    font-size: 8px;
  }
  .be-map__pin-title {
    font-size: 0.6875rem;
  }
  .be-map__pin-chevron {
    font-size: 8px;
  }
  .be-map__control--step {
    display: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/be/v3/digital-map/MapViewport.vue